import { useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import Section, { SectionHeading } from "../ui/Section";
import { useApp } from "../../context/AppContext";
import { projects } from "../../data/content";
import type { ProjectCategory, ProjectStatus } from "../../data/types";

const FILTERS: (ProjectCategory | "all")[] = ["all", "frontend", "fullstack", "ai", "backend"];

const statusStyles: Record<ProjectStatus, string> = {
  production: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
  prototype: "border-cyan-400/40 bg-cyan-400/10 text-cyan-300",
  lab: "border-purple-400/40 bg-purple-400/10 text-purple-300",
  experimental: "border-amber-400/40 bg-amber-400/10 text-amber-300",
};

export default function Projects() {
  const { t, locale } = useApp();
  const [filter, setFilter] = useState<ProjectCategory | "all">("all");

  const filtered = useMemo(
    () => (filter === "all" ? projects : projects.filter((p) => p.category === filter)),
    [filter]
  );

  return (
    <Section id="projects">
      <SectionHeading tag={t("projects.tag")} title={t("projects.title")} subtitle={t("projects.subtitle")} />

      {/* Filter chips */}
      <div className="mb-8 flex flex-wrap gap-2" role="tablist" aria-label={t("projects.title")}>
        {FILTERS.map((f) => {
          const active = filter === f;
          return (
            <button
              key={f}
              role="tab"
              aria-selected={active}
              onClick={() => setFilter(f)}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-all ${
                active
                  ? "border-cyan-400/50 bg-cyan-400/15 text-cyan-300"
                  : "border-soft card-bg text-soft hover:border-cyan-400/40 hover:text-cyan-400"
              }`}
            >
              {t(`projects.filter.${f}`)}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <p className="text-soft">{t("projects.empty")}</p>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2">
          {filtered.map((p) => (
            <article
              key={p.id}
              className="group relative flex flex-col overflow-hidden rounded-3xl border border-soft card-bg p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-cyan-400/40 hover:shadow-2xl hover:shadow-cyan-500/10"
            >
              <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-cyan-500/10 opacity-0 blur-3xl transition-opacity duration-300 group-hover:opacity-100" />
              <div className="mb-4 flex items-start justify-between gap-3">
                <span
                  className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${statusStyles[p.status]}`}
                >
                  {t(`status.${p.status}`)}
                </span>
              </div>
              <h3 className="font-display text-xl font-bold leading-snug">{p.title[locale]}</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-soft">{p.description[locale]}</p>
              <div className="mt-5 flex flex-wrap gap-1.5">
                {p.stack.map((s) => (
                  <span key={s} className="rounded-md border border-soft px-2 py-0.5 font-mono text-xs text-soft">
                    {s}
                  </span>
                ))}
              </div>
              <button
                type="button"
                aria-label={`${t("projects.detail")}: ${p.title[locale]}`}
                className="mt-6 inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-cyan-400 transition-colors hover:text-cyan-300"
              >
                {t("projects.detail")}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </article>
          ))}
        </div>
      )}
    </Section>
  );
}
